import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Users, 
  Calendar, 
  ArrowLeft, 
  Edit, 
  Trash2, 
  FileText,
  Clock,
  ChevronRight
} from 'lucide-react';
import { jobBoardApi } from '../../api/jobBoardApi';
import { JobDto } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const JobDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;
      try {
        const response = await jobBoardApi.getJob(id);
        setJob(response.data);
      } catch (error) {
        console.error("Error fetching job details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  const handleDelete = async () => {
    if (!id || !window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await jobBoardApi.deleteJob(id);
      navigate('/job-board/jobs');
    } catch (err) {
      console.error("Failed to delete job:", err);
      alert("Could not delete job position.");
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;
  if (!job) return <div className="p-8 text-center text-slate-500">Job not found.</div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-6 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <ChevronRight size={14} />
        <Link to="/job-board/overview" className="hover:text-blue-600">Job Board</Link>
        <ChevronRight size={14} />
        <Link to="/job-board/jobs" className="hover:text-blue-600">Job Listings</Link>
        <ChevronRight size={14} />
        <span className="text-slate-800 truncate">{job.jobTitle}</span>
      </nav>

      {/* Header */}
      <header className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Briefcase className="text-indigo-600" size={24} />
              {job.jobTitle}
            </h1>
            <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
              <Clock size={14} /> Posted on {new Date(job.postedDate).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Link 
            to={`/job-board/jobs/${job.jobId}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
          >
            <Edit size={16} /> Edit
          </Link>
          <button 
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-100 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-100 transition-all shadow-sm"
          >
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Job Description
            </h2>
            <div className="text-slate-600 leading-relaxed whitespace-pre-wrap">{job.jobDescription}</div>
          </section>

          <section className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Briefcase size={20} className="text-indigo-600" />
              Qualifications
            </h2>
            <div className="text-slate-600 leading-relaxed whitespace-pre-wrap">{job.qualifications}</div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl shadow-blue-900/10">
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-6">Recruitment Stats</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-slate-800 rounded-lg"><Users size={20} className="text-blue-400" /></div>
                <div>
                  <p className="text-2xl font-bold">{job.numberOfPeopleNeeded}</p>
                  <p className="text-slate-400 text-xs font-medium uppercase tracking-tight">Vacancies</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-slate-800 rounded-lg"><Calendar size={20} className="text-blue-400" /></div>
                <div>
                  <p className="text-sm font-bold">{new Date(job.postedDate).toLocaleDateString()}</p>
                  <p className="text-slate-400 text-xs font-medium uppercase tracking-tight">Posted Date</p>
                </div>
              </div>
            </div>
            <Link to={`/job-board/applicants/job/${job.jobId}`} className="w-full mt-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2">
              Manage Applicants <ChevronRight size={16} />
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
};
export default JobDetail;